// Voice (spec "Voice"): the voice keys (Settings › Voice) or a Voice button start recording
// (ui/voice.ts) and the pill shows what the hub hears so far (VoiceCard). A tap goes on
// hands-free until Insert (✓, Return, the hands-free key or the button again); a hold is
// push-to-talk, inserting on release. The
// final transcript is pasted into the session once, like a clipboard paste (bracketed when the
// app asked for it), never with Enter. Esc, ✕, or sliding off the button cancels.
//
// The recording streams to the hub as it's heard (VoiceStream): the pill shows the words it
// sends back while recording, and the final text follows the release (at once when the hub
// already heard it all).

import { useSyncExternalStore } from "react";
import { peekTerm } from "../term/manager";
import { setCtrl } from "../term/modifiers";
import { MicError, Recorder, VoiceStream } from "./voice";
import { release } from "./voiceGesture";

/** What a push-to-talk that caught nothing says: how to use the key. */
export const HOLD_HINT = "Hold to talk, or tap for hands-free";
const HINT_MS = 1600;
const ERROR_MS = 6000;

export type VoicePhase =
  "idle" | "starting" | "recording" | "finishing" | "hint" | "error";

export interface VoiceState {
  phase: VoicePhase;
  /** The session the text goes to (its pane shows the card). */
  sid: number | null;
  /** The latest transcript (live while recording). */
  text: string;
  /** Recording stopped at MAX_SECONDS; waiting for the release. */
  capped: boolean;
  /** Tapped: listening with nothing held, until inserted or cancelled. */
  handsFree: boolean;
  /** Held and slid far enough left: letting go cancels (the shared rule, voiceGesture.ts). */
  cancelling: boolean;
  /** When recording started (performance.now), for the timer. */
  since: number;
  /** What a hint or an error pill says. */
  message: string | null;
}

const IDLE: VoiceState = {
  phase: "idle",
  sid: null,
  text: "",
  capped: false,
  handsFree: false,
  cancelling: false,
  since: 0,
  message: null,
};

let state = IDLE;
const listeners = new Set<() => void>();
function show(next: VoiceState) {
  state = next;
  listeners.forEach((l) => l());
}
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => void listeners.delete(fn);
};

export const voiceState = () => state;
export const useVoice = () => useSyncExternalStore(subscribe, voiceState);
/** Listening or transcribing (a new hold waits until it's done). */
const voiceBusy = () =>
  state.phase === "starting" ||
  state.phase === "recording" ||
  state.phase === "finishing";

let rec: Recorder | null = null;
let stream: VoiceStream | null = null;
let dismiss: ReturnType<typeof setTimeout> | null = null;

/** The live microphone level, 0..1 (the pill's meter reads it every frame). */
export const voiceLevel = () => rec?.level ?? 0;

/** Stop recording, drop the stream, hide the pill. */
function reset() {
  rec?.stop();
  rec = null;
  stream?.cancel();
  stream = null;
  if (dismiss) clearTimeout(dismiss);
  dismiss = null;
  if (state !== IDLE) show(IDLE);
}

/** Show the pill with a hint or an error for a while. */
function flash(
  sid: number,
  phase: "hint" | "error",
  message: string | null = null,
) {
  reset();
  show({ ...IDLE, phase, sid, message });
  dismiss = setTimeout(reset, phase === "error" ? ERROR_MS : HINT_MS);
}

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/**
 * Start listening for session `sid`. Call it within the press: some browsers only start audio
 * in a user gesture. False when there's nothing to talk to (no live session there, or busy).
 */
export function startVoice(sid: number | null, at: number = performance.now()): boolean {
  const t = sid === null ? undefined : peekTerm(sid);
  if (!t || t.exitCode !== null || voiceBusy()) return false;
  reset();
  pressedAt = at;
  setCtrl(false); // an armed key-bar Ctrl would apply to the pasted text
  let r: Recorder;
  try {
    r = new Recorder();
  } catch (e) {
    flash(t.sid, "error", errText(e));
    return true;
  }
  rec = r;
  const st = new VoiceStream(); // connects while the microphone comes up
  stream = st;
  r.onAudio = (pcm) => st.send(pcm);
  st.onPartial = (text) => {
    if (stream === st && state.phase === "recording" && text) show({ ...state, text });
  };
  show({ ...IDLE, phase: "starting", sid: t.sid });
  r.onLimit = () => {
    if (rec !== r) return;
    if (state.handsFree)
      void finishVoice(); // nothing will be released: insert at the limit
    else show({ ...state, capped: true });
  };
  r.ready.then(
    () => {
      if (rec !== r) return;
      show({ ...state, phase: "recording", since: performance.now() });
    },
    (e: unknown) => {
      if (rec === r)
        flash(
          t.sid,
          "error",
          e instanceof MicError
            ? e.message
            : `Couldn’t start the microphone: ${errText(e)}`,
        );
    },
  );
  return true;
}

/** When the press that started this recording happened (its event's timeStamp). */
let pressedAt = 0;

/** Let go of the key or button at `at` (the release event's timeStamp), moved by dx, dy from
 * the press: the shared rule (voiceGesture.ts) says hands-free (a tap, or before the
 * microphone came up), insert (a hold) or cancel (slid left). */
export function releaseVoice(at: number = performance.now(), dx = 0, dy = 0) {
  if (!rec || state.handsFree) return;
  const r = release((at - pressedAt) / 1000, dx, dy, state.phase === "starting");
  if (r === "handsFree") show({ ...state, handsFree: true });
  else if (r === "cancel") cancelVoice();
  else void finishVoice();
}

/** While held: whether letting go now would cancel (slid far enough left). */
export function setVoiceCancelling(on: boolean) {
  if (rec && !state.handsFree && state.cancelling !== on) show({ ...state, cancelling: on });
}

/** Slid up to the lock: hands-free from here, however long it was held. */
export function lockVoice() {
  if (rec && (state.phase === "starting" || state.phase === "recording"))
    show({ ...state, handsFree: true });
}

/** Insert: the hub's text of the recording, pasted into the session once. */
export async function finishVoice() {
  const r = rec;
  const st = stream;
  const sid = state.sid;
  if (!r || !st || sid === null) return;
  if (state.phase !== "recording") return;
  const pushToTalk = !state.handsFree;
  r.stop();
  show({ ...state, phase: "finishing" });
  try {
    const text = await st.end();
    if (stream !== st) return; // cancelled meanwhile
    stream = null;
    reset();
    if (text) peekTerm(sid)?.paste(text);
    else flash(sid, "hint", pushToTalk ? HOLD_HINT : "Didn’t catch that. Try again.");
  } catch (e) {
    if (stream === st) flash(sid, "error", errText(e));
  }
}

/** Esc, a slide off the button, the window losing focus: drop the clip, insert nothing. */
export function cancelVoice() {
  reset();
}
